"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Link from "next/link";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";


const API = process.env.NEXT_PUBLIC_API_URL;
if (!API) {
  throw new Error("NEXT_PUBLIC_API_URL is not defined");
}



/* =====================================================
   MAIN PAGE
===================================================== */

export default function AnalyticsPage() {

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {

    fetch(`${API}/api/admin/analytics/summary`, {
      credentials: "include",
      cache: "no-store",
    })
      .then(r => r.json())
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));

  }, []);


  /* ================= LOADING ================= */

  if (loading) {

    return (
      <div className="min-h-screen bg-[#030011] flex items-center justify-center">

        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-cyan-300 text-lg tracking-wide"
        >
          Initializing Intelligence Engine...
        </motion.div>

      </div>
    );
  }


  if (!data) {

    return (
      <div className="min-h-screen bg-[#030011] flex items-center justify-center text-red-400">
        Analytics system unavailable
      </div>
    );
  }


  /* =====================================================
     UI
  ===================================================== */

  return (

    <div className="min-h-screen bg-[#030011] text-white px-8 py-24">


      {/* ================= HEADER ================= */}

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="max-w-7xl mx-auto mb-20 text-center"
      >

        <h1
          className="
            text-5xl md:text-6xl font-extrabold
            bg-gradient-to-r from-violet-400 to-cyan-400
            bg-clip-text text-transparent
            tracking-tight
          "
        >
          CompuBio Intelligence Center
        </h1>

        <p className="mt-6 text-lg opacity-80 max-w-3xl mx-auto leading-8">
          Real-time scientific traffic intelligence and institutional lead
          discovery for platform growth and strategic partnerships.
        </p>
<div className="mt-10 flex justify-center gap-6">

  {/* Visitors */}
  <Link
    href="/admin/analytics/visitors"
    className="
      group relative px-6 py-3 rounded-xl
      border border-cyan-400/30
      bg-black/60 backdrop-blur
      text-cyan-300 font-semibold
      transition-all

      hover:border-cyan-400
      hover:text-white
      hover:shadow-[0_0_30px_rgba(34,211,238,0.25)]
    "
  >
    <span className="relative z-10">
      👁 Visitor Explorer
    </span>

    {/* glow */}
    <span
      className="
        absolute inset-0 rounded-xl
        bg-cyan-400/10 blur-lg
        opacity-0 group-hover:opacity-100
        transition
      "
    />
  </Link>


  {/* Optional: future CRM */}
  <button
    disabled
    className="
      px-6 py-3 rounded-xl
      border border-violet-400/20
      bg-black/40
      text-violet-300/50
      cursor-not-allowed
    "
  >
   <Link
  href="/admin/crm"
  className="px-6 py-3 rounded-xl border border-violet-400/30
             bg-black/60 text-violet-300 hover:text-white"
>
  🧠 CRM Engine
</Link>
  </button>

</div>

      </motion.div>


      {/* ================= STATS ================= */}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8 mb-24"
      >

        <StatCard title="Total Visits" value={data.total} />

        <StatCard
          title="Top Country"
          value={data.countries?.[0]?.country || "-"}
        />

        <StatCard
          title="Top ASN"
          value={data.leads?.[0]?.asn || "-"}
        />

        <StatCard
          title="Last Update"
          value={new Date(data.generated_at).toLocaleTimeString()}
        />

      </motion.div>


      {/* ================= CHART ================= */}

      <Section title="Global Traffic Distribution">

        <ResponsiveContainer width="100%" height={300}>

          <BarChart data={data.countries || []}>

            <XAxis
              dataKey="country"
              stroke="#9ca3af"
              tick={{ fontSize: 12 }}
            />

            <YAxis stroke="#9ca3af" />

            <Tooltip
              contentStyle={{
                background: "#020617",
                border: "1px solid #22d3ee55",
                borderRadius: "12px",
              }}
            />

            <Bar
              dataKey="hits"
              fill="url(#cyanGradient)"
              radius={[8, 8, 0, 0]}
            />

            <defs>
              <linearGradient
                id="cyanGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>

          </BarChart>

        </ResponsiveContainer>

      </Section>


      {/* ================= LEADS ================= */}

      <Section title="Institutional & Enterprise Leads">

        <div className="grid md:grid-cols-2 gap-6">

          {(data.leads || []).map((l: any) => (

            <motion.div
              key={l.asn}
              whileHover={{ scale: 1.02 }}
              className="
                relative p-6 rounded-2xl
                border border-cyan-400/20
                bg-black/50 backdrop-blur
                shadow-[0_0_40px_rgba(34,211,238,0.08)]
              "
            >

              {/* glow */}
              <div
                className="
                  absolute inset-0 rounded-2xl
                  bg-cyan-400/5 blur-xl
                  pointer-events-none
                "
              />

              <div className="relative z-10">

                <div className="text-cyan-300 font-semibold text-lg">
                  ASN {l.asn}
                </div>

                <div className="text-sm opacity-80 mt-1">
                  {l.org || "Unknown Organization"}
                </div>

                <div className="mt-3 text-xs text-violet-300">
                  {l.hits} qualified visits
                </div>

              </div>

            </motion.div>

          ))}

        </div>

      </Section>

    </div>
  );
}


/* =====================================================
   UI COMPONENTS
===================================================== */


function StatCard({ title, value }: any) {

  return (

    <div
      className="
        relative rounded-3xl p-7
        bg-gradient-to-br from-black/70 via-[#020617] to-black/80
        border border-cyan-400/25
        shadow-[0_0_50px_rgba(34,211,238,0.12)]
      "
    >

      {/* glow */}
      <div
        className="
          absolute inset-0 rounded-3xl
          bg-cyan-400/10 blur-2xl
          pointer-events-none
        "
      />

      <div className="relative z-10">

        <div className="text-sm opacity-70 mb-2">
          {title}
        </div>

        <div className="text-3xl font-bold text-cyan-300 truncate">
          {value}
        </div>

      </div>

    </div>
  );
}


function Section({ title, children }: any) {

  return (

    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="max-w-7xl mx-auto mb-28"
    >

      <h2
        className="
          text-2xl font-semibold mb-6
          text-violet-300
          tracking-tight
        "
      >
        {title}
      </h2>

      <div
        className="
          relative rounded-3xl p-8
          bg-black/40 backdrop-blur
          border border-violet-400/20
          shadow-[0_0_60px_rgba(139,92,246,0.12)]
        "
      >
        {children}
      </div>

    </motion.div>

  );
}
